<template>
  <div class="container mx-auto px-4 py-8">
    <div v-if="loading" class="flex justify-center py-12">
      <div class="spinner w-8 h-8"></div>
    </div>

    <div v-else-if="notice" class="max-w-4xl mx-auto">
      <!-- 헤더 -->
      <div class="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div class="flex justify-between items-start mb-4">
          <div>
            <span class="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full mb-3">
              공지사항
            </span>
            <h1 class="text-2xl font-bold text-gray-900">{{ notice.title }}</h1>
          </div>
          
          <!-- 관리 버튼 (관리자만) -->
          <div v-if="authStore.isAdmin" class="flex gap-2">
            <RouterLink
              :to="`/notices/${notice.id}/edit`"
              class="btn btn-outline-primary btn-sm"
            >
              <PencilIcon class="w-4 h-4 mr-2" />
              수정
            </RouterLink>
            <button
              @click="deleteNotice"
              class="btn btn-outline-danger btn-sm"
            >
              <TrashIcon class="w-4 h-4 mr-2" />
              삭제
            </button>
          </div>
        </div>

        <!-- 메타 정보 -->
        <div class="flex items-center text-sm text-gray-600 space-x-4 mb-6">
          <div class="flex items-center">
            <UserIcon class="w-4 h-4 mr-1" />
            {{ notice.author.name }}
          </div>
          <div class="flex items-center">
            <CalendarIcon class="w-4 h-4 mr-1" />
            {{ formatDate(notice.createdAt) }}
          </div>
          <div v-if="notice.updatedAt !== notice.createdAt" class="flex items-center">
            <ClockIcon class="w-4 h-4 mr-1" />
            수정됨: {{ formatDate(notice.updatedAt) }}
          </div>
        </div>
      </div>

      <!-- 본문 -->
      <div class="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div class="prose prose-lg max-w-none" v-html="notice.content"></div>
      </div>

      <!-- 액션 버튼 -->
      <div class="flex justify-between">
        <RouterLink to="/notices" class="btn btn-outline btn-sm">
          <ArrowLeftIcon class="w-4 h-4 mr-2" />
          목록으로 돌아가기
        </RouterLink>
      </div>
    </div>

    <div v-else class="text-center py-12">
      <ExclamationTriangleIcon class="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 class="text-lg font-medium text-gray-900 mb-2">공지사항을 찾을 수 없습니다</h3>
      <p class="text-gray-600 mb-6">요청하신 공지사항이 존재하지 않거나 삭제되었을 수 있습니다.</p>
      <RouterLink to="/notices" class="btn btn-primary btn-sm">
        공지사항 목록으로
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import {
  PencilIcon,
  TrashIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import { useBreadcrumbStore } from '@/stores/breadcrumbs'
import { noticeApi } from '@/services/api'
import type { Notice } from '@/types'
import { useToast } from 'vue-toastification'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const breadcrumbStore = useBreadcrumbStore()
const toast = useToast()

const notice = ref<Notice | null>(null)
const loading = ref(false)

const noticeId = computed(() => {
  return Number(route.params.id)
})

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const loadNotice = async () => {
  try {
    loading.value = true
    const response = await noticeApi.getNotice(noticeId.value)
    notice.value = response.data
    breadcrumbStore.setLabel('NoticeDetail', notice.value.title)
  } catch (error: any) {
    console.error('Failed to load notice:', error)
    if (error.response?.status === 404) {
      toast.error('공지사항을 찾을 수 없습니다.')
    } else {
      toast.error('공지사항을 불러오는데 실패했습니다.')
    }
  } finally {
    loading.value = false
  }
}

const deleteNotice = async () => {
  if (!notice.value) return
  if (!confirm('정말 삭제하시겠습니까?')) return

  try {
    await noticeApi.deleteNotice(notice.value.id)
    toast.success('공지사항이 삭제되었습니다.')
    router.push('/notices')
  } catch (error: any) {
    console.error('Failed to delete notice:', error)
    toast.error('공지사항 삭제에 실패했습니다.')
  }
}

onMounted(() => {
  if (noticeId.value) {
    loadNotice()
  }
})
</script>

<style scoped>
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose h1, .prose h2, .prose h3, .prose h4 {
  color: #111827;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.prose p {
  margin-bottom: 1.25rem;
}

.prose ul, .prose ol {
  margin-bottom: 1.25rem;
  padding-left: 1.5rem;
}

.prose blockquote {
  border-left: 4px solid #e5e7eb;
  padding-left: 1rem;
  font-style: italic;
  color: #6b7280;
  margin: 1.5rem 0;
}

.prose img {
  border-radius: 0.5rem;
  margin: 1.5rem 0;
}

.prose a {
  color: #3b82f6;
  text-decoration: underline;
}

.prose a:hover {
  color: #1d4ed8;
}

.prose pre {
  background-color: #f3f4f6;
  border-radius: 0.5rem;
  padding: 1rem;
  overflow-x: auto;
  margin: 1.5rem 0;
}

.prose code {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
}
</style>
